<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import MultiSelect from '@/components/multi-select'
import i18n from '@/i18n'
import { required, email } from 'vuelidate/lib/validators'
import { mapState } from 'vuex'
import { ddlHelper } from '@/utils'
import { tenantService } from '@/helpers/services/tenant.service'
import { ROLE_MODULE } from '@/state/modules/role'
import { usersMethods, roleMethods } from '@/state/helpers'

/**
 * Create and edit, in one component.
 *
 * one_sales ships userCreate.vue and userUpdate.vue as near-identical files,
 * and they have drifted — the update one silently lost the agent-code field at
 * some point. The two differ in three things (the title, whether it loads
 * first, which action it dispatches), so they are one component keyed off
 * `$route.params.id`.
 *
 * ── No password field ───────────────────────────────────────────────────
 * Deliberate, and the screen says so. The server creates the account with a
 * random hash nobody holds and mails a first-time-login link; an operator
 * typing a password means the operator knows it. The Team screen (/team) is
 * the deliberate exception, for a brand with no mail server.
 */
export default {
  page() {
    return { title: this.title }
  },
  components: { Layout, PageHeader, MultiSelect },
  data() {
    return {
      form: {
        name: '',
        email: '',
        roleUuid: '',
        isAdmin: false,
        /*
         * Typed by the operator, who then hands it over in person.
         *
         * This screen used to mail a first-time-login link and save a hash
         * nobody held, which is the better shape only when there is a mail
         * server — and there isn't one, so what the operator got was an
         * account nobody could sign in to. On EDIT an empty box means "leave
         * their password alone".
         */
        password: '',
        // undefined, not []: the service only sends `tenant_ids` when the form
        // carries the field, and only platform staff get it. A supervisor
        // sending an empty list would be asking for a platform seat, which the
        // server trims back to their own brands.
        //
        // A LIST because an agent can work on several — a supervisor across two
        // of an operator's sites, an outsourced team covering three.
        tenantIds: undefined,
      },
      tenantDdl: [],
      showPassword: false,
      isPlatformStaff: false,
      loading: false,
      submitting: false,
    }
  },
  validations: {
    form: {
      name: { required },
      email: { required, email },
      /*
       * Required on create, optional on edit — an empty box there means "leave
       * it alone", which is what saving a rename has to do.
       *
       * Ten characters, matching the server, so the operator finds out before
       * the round trip rather than after.
       */
      password: {
        required(value) {
          return this.isEdit || !!value
        },
        longEnough(value) {
          return !value || value.length >= 10
        },
      },
    },
  },
  computed: {
    ...mapState(ROLE_MODULE, { roleRows: (state) => state.list.data }),
    isEdit() {
      return !!this.$route.params.id
    },
    title() {
      return this.isEdit
        ? i18n.t('label.updateX', [i18n.t('module.user')])
        : i18n.t('label.createX', [i18n.t('module.user')])
    },
    breadcrumbItems() {
      return [
        { text: i18n.t('module.admin'), href: '/' },
        { text: i18n.t('module.user'), href: '/users' },
        { text: this.title, active: true },
      ]
    },
    roleDdl() {
      // Active roles only: UserHelpers loads only active ones at login, so
      // assigning an inactive role grants nothing and reads as broken.
      return this.roleRows.filter((r) => r.isActive).map((r) => ({ text: r.name, value: r.id }))
    },
  },
  async created() {
    this.loading = true

    try {
      await Promise.all([
        this.getAllRole({ filter: { pageNumber: 1, pageSize: 100 } }),
        this.loadTenants(),
      ])

      if (this.isEdit) await this.seedFromServer()
    } finally {
      this.loading = false
    }
  },
  methods: {
    ...usersMethods,
    ...roleMethods,

    /**
     * A password worth reading down a phone.
     *
     * Deliberately not a random base64 blob: the operator has to say this out
     * loud or paste it into a chat, and `aX7/+qK0` becomes a support ticket
     * about a password that "does not work". Three short words plus digits is
     * long, easy to dictate, and still well past anything guessable.
     */
    suggestPassword() {
      const words = [
        'amber', 'bamboo', 'cobalt', 'dune', 'ember', 'fjord', 'gravel', 'harbour',
        'indigo', 'jasmine', 'kettle', 'lantern', 'marble', 'nutmeg', 'orchid', 'pebble',
      ]
      const pick = () => words[Math.floor(Math.random() * words.length)]
      const digits = String(Math.floor(Math.random() * 90) + 10)

      this.form.password = `${pick()}-${pick()}-${pick()}${digits}`
      // Shown, because a password nobody can read is a password nobody can
      // pass on.
      this.showPassword = true
    },

    async loadTenants() {
      try {
        const result = await tenantService.list()
        this.isPlatformStaff = !!result.is_platform_staff
        // No "platform" pseudo-option here: on a FORM, leaving the brand empty
        // already means a platform seat. The pseudo-option exists on the
        // listing's filter because `tenant_id[]=null` matches nothing in SQL.
        this.tenantDdl = ddlHelper.getTenantOptions(result.data || [], false)

        // Only staff may choose, so only they get the field at all. An empty
        // list on create is a platform seat, which is the one thing only they
        // can make.
        if (this.isPlatformStaff && !this.isEdit) this.form.tenantIds = []
      } catch (e) {
        this.tenantDdl = []
      }
    },

    async seedFromServer() {
      const result = await this.getUsers({ id: this.$route.params.id })
      const d = result && result.data

      if (!d) return

      this.form.name = d.name || ''
      this.form.email = d.email || ''
      this.form.roleUuid = (d.roles || [])[0] ? d.roles[0].uuid : ''
      this.form.isAdmin = !!d.is_admin

      if (this.isPlatformStaff) this.form.tenantIds = d.tenant_ids || []
    },

    async submit() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) return

      this.submitting = true

      try {
        const result = this.isEdit
          ? await this.updateUsers({ id: this.$route.params.id, data: this.form })
          : await this.createUsers({ data: this.form })

        // The store's actions RESOLVE with undefined after alerting a failure
        // (see state/listing.store.js), so this is how a form stays put on an
        // error instead of navigating away from it.
        if (result) this.$router.push({ name: 'Users-listing' })
      } finally {
        this.submitting = false
      }
    },

    cancel() {
      this.$router.push({ name: 'Users-listing' })
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />
    <div class="row">
      <!-- col-12: the card spans the page like every listing does, and the
           fields sit in a grid inside it. A narrow column left two thirds of a
           wide screen empty; a full-width column with one field per row is
           worse still, because a 40-character name input stretched across
           1400px is harder to scan, not easier. -->
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-form @submit.prevent="submit">
              <b-row>
                <b-col cols="12" md="6" lg="4">
                  <b-form-group :label="$t('label.name')" label-for="name">
                    <b-form-input
                      id="name"
                      v-model.trim="form.name"
                      :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
                    />
                    <b-form-invalid-feedback>
                      {{ $t('message.requiredX', [$t('label.name')]) }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </b-col>

                <b-col cols="12" md="6" lg="4">
                  <b-form-group :label="$t('label.email')" label-for="email">
                    <b-form-input
                      id="email"
                      v-model.trim="form.email"
                      type="email"
                      :state="$v.form.email.$dirty ? !$v.form.email.$error : null"
                    />
                    <b-form-invalid-feedback>
                      {{ $t('user.emailInvalid') }}
                    </b-form-invalid-feedback>
                    <small class="form-text text-muted">{{ $t('user.emailHint') }}</small>
                  </b-form-group>
                </b-col>

                <b-col cols="12" md="6" lg="4">
                  <b-form-group :label="$t('label.password')" label-for="password">
                    <b-input-group>
                      <b-form-input
                        id="password"
                        v-model="form.password"
                        :type="showPassword ? 'text' : 'password'"
                        autocomplete="new-password"
                        :placeholder="isEdit ? $t('user.passwordKeepHint') : ''"
                        :state="$v.form.password.$dirty ? !$v.form.password.$error : null"
                      />
                      <b-input-group-append>
                        <!-- Visible by default would be wrong; unmaskable would
                             be worse. The operator is reading this out to the
                             person, so they have to be able to see it. -->
                        <b-button variant="outline-secondary" @click="showPassword = !showPassword">
                          <i class="mdi" :class="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"></i>
                        </b-button>
                        <b-button variant="outline-secondary" @click="suggestPassword">
                          {{ $t('team.suggest') }}
                        </b-button>
                      </b-input-group-append>
                      <b-form-invalid-feedback :state="$v.form.password.$dirty ? !$v.form.password.$error : null">
                        {{ $t('user.passwordTooShort') }}
                      </b-form-invalid-feedback>
                    </b-input-group>
                    <small class="form-text text-muted">
                      {{ isEdit ? $t('user.passwordEditHint') : $t('user.passwordCreateHint') }}
                    </small>
                  </b-form-group>
                </b-col>

                <b-col v-if="isPlatformStaff" cols="12" md="6" lg="4">
                  <b-form-group :label="$t('label.brand')" label-for="brand">
                    <MultiSelect
                      id="brand"
                      v-model="form.tenantIds"
                      :options="tenantDdl"
                      :multiple="true"
                      :hint="$t('user.brandHint')"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12" md="6" lg="4">
                  <b-form-group :label="$t('label.role')" label-for="role">
                    <MultiSelect
                      id="role"
                      v-model="form.roleUuid"
                      :options="roleDdl"
                      :multiple="false"
                      :hint="$t('user.roleHint')"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12" md="6" lg="4">
                  <b-form-group>
                    <b-form-checkbox v-model="form.isAdmin" switch>
                      {{ $t('user.managesTeam') }}
                      <small class="d-block text-muted">{{ $t('user.managesTeamHint') }}</small>
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>

              </b-row>

              <div class="text-right">
                <b-button variant="light" class="mr-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
                <b-button type="submit" variant="primary" :disabled="submitting || loading">
                  {{ $t('label.submit') }}
                </b-button>
              </div>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
